import React, { useEffect, useRef, useState } from 'react'
import aiKnowledgeBase from '../../data/aiKnowledgeBase'
import { GEMINI_API_KEY } from '../../config/gemini'
import './CapabilityChat.scss'

const modelName = 'gemini-2.5-flash'

const suggestions = [
  'What are his strongest technical capabilities?',
  'Tell me about his AI and automation work.',
  'What data engineering has he done?',
  'How can I contact Paradzai?'
]

const CapabilityChat = () => {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Hi 👋 I\'m Paradzai\'s portfolio assistant. Ask me about his engineering experience, skills, projects or the kind of problems he can help solve.' }
  ])
  const [question, setQuestion] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const scrollRef = useRef(null)

  useEffect(() => {
    const box = scrollRef.current
    if (box) box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' })
  }, [messages, isLoading])

  const ask = async (rawQuestion) => {
    const trimmedQuestion = rawQuestion.trim()
    const apiKey = GEMINI_API_KEY

    if (!trimmedQuestion || isLoading) return

    setMessages((current) => [...current, { role: 'user', text: trimmedQuestion }])
    setQuestion('')
    setError('')

    if (!apiKey) {
      setMessages((current) => [...current, { role: 'assistant', text: 'The assistant is not connected yet. Please contact Paradzai directly at paradzaigwira@gmail.com or WhatsApp +263 71 533 6984.' }])
      return
    }

    setIsLoading(true)

    const knowledge = JSON.stringify(aiKnowledgeBase)
    const instruction = `You are Paradzai Gwira's professional portfolio assistant. Follow these rules exactly:\n${aiKnowledgeBase.responsePolicy.map((rule) => `- ${rule}`).join('\n')}\n\nVerified knowledge base:\n${knowledge}`
    const conversation = messages.slice(-6).map((message) => ({
      role: message.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: message.text }]
    }))
    conversation.push({ role: 'user', parts: [{ text: trimmedQuestion }] })

    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${encodeURIComponent(apiKey)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          systemInstruction: { parts: [{ text: instruction }] },
          contents: conversation,
          generationConfig: { temperature: 0.2, maxOutputTokens: 350 }
        })
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error?.message || 'Gemini request failed')

      const answer = payload.candidates?.[0]?.content?.parts?.map((part) => part.text || '').join('').trim()
      if (!answer) throw new Error('Gemini returned no answer')
      setMessages((current) => [...current, { role: 'assistant', text: answer }])
    } catch (requestError) {
      setError('The assistant is unavailable right now. Please try again shortly.')
      setMessages((current) => [...current, { role: 'assistant', text: 'I cannot verify that answer right now. Please contact Paradzai directly at paradzaigwira@gmail.com or WhatsApp +263 71 533 6984.' }])
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = (event) => {
    event.preventDefault()
    ask(question)
  }

  return (
    <section className="capability-chat" id="ask">
      <div className="pf-container chat-layout">
        <div className="chat-heading" data-reveal>
          <p className="pf-eyebrow"><span>07</span> Ask my AI</p>
          <h2>Don&apos;t read the résumé. <span className="pf-gradient-text">Interrogate it.</span></h2>
          <p>This assistant is grounded in a verified knowledge base about my work. It won&apos;t make things up; if it doesn&apos;t know, it&apos;ll point you to me directly.</p>
          <ul className="chat-facts">
            <li><i className="fas fa-shield-halved" /> Grounded answers only</li>
            <li><i className="fas fa-bolt" /> Powered by Gemini 2.5 Flash</li>
            <li><i className="fas fa-user-check" /> Hands off to a human when unsure</li>
          </ul>
        </div>

        <div className="chat-shell" data-reveal style={{ '--delay': '120ms' }}>
          <div className="chat-shell__bar">
            <span className="chat-avatar"><i className="fas fa-robot" /></span>
            <div>
              <strong>PG Assistant</strong>
              <small><span className="chat-online" /> Online · answers in seconds</small>
            </div>
          </div>

          <div className="chat-messages" aria-live="polite" ref={scrollRef}>
            {messages.map((message, index) => (
              <div className={`chat-message chat-message-${message.role}`} key={`${message.role}-${index}`}>
                <p>{message.text}</p>
              </div>
            ))}
            {isLoading && (
              <div className="chat-message chat-message-assistant chat-typing" aria-label="Assistant is typing">
                <span /><span /><span />
              </div>
            )}
          </div>

          {messages.length < 3 && (
            <div className="chat-suggestions">
              {suggestions.map((text) => (
                <button type="button" key={text} onClick={() => ask(text)} disabled={isLoading}>{text}</button>
              ))}
            </div>
          )}

          <form className="chat-form" onSubmit={onSubmit}>
            <label htmlFor="capability-question" className="visually-hidden">Ask a question</label>
            <input
              id="capability-question"
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="Ask anything about my experience…"
              disabled={isLoading}
              autoComplete="off"
            />
            <button type="submit" disabled={isLoading || !question.trim()} aria-label="Send question">
              <i className="fas fa-arrow-up" />
            </button>
          </form>
          {error && <small className="chat-error">{error}</small>}
        </div>
      </div>
    </section>
  )
}

export default CapabilityChat
